import { useEffect, useState, useRef, useCallback } from "react";
import { motion, useMotionValue, useTransform, useSpring, AnimatePresence } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

const Hero = () => {
  const rotatingWords = ["experiences", "possibilities", "solutions", "results", "innovation"];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const containerRef = useRef(null);

  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);

  const smoothX = useSpring(mouseX, { stiffness: 50, damping: 30 });
  const smoothY = useSpring(mouseY, { stiffness: 50, damping: 30 });

  // Parallax transforms at different depths
  const bgX = useTransform(smoothX, [0, 1], [70, -70]);
  const bgY = useTransform(smoothY, [0, 1], [70, -70]);
  const midX = useTransform(smoothX, [0, 1], [45, -45]);
  const midY = useTransform(smoothY, [0, 1], [45, -45]);
  const fgX = useTransform(smoothX, [0, 1], [25, -25]);
  const fgY = useTransform(smoothY, [0, 1], [25, -25]);

  // Gradient position
  const gradX = useTransform(smoothX, [0, 1], [20, 80]);
  const gradY = useTransform(smoothY, [0, 1], [20, 80]);

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width);
    mouseY.set((e.clientY - rect.top) / rect.height);
  }, [mouseX, mouseY, containerRef]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [rotatingWords.length]);

  const scrollToProjects = () => {
    document.querySelector("#projects")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 bg-[#060608]"
    >
      {/* Layer 1: Cursor-reactive gradient mesh */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: useTransform(
            [gradX, gradY],
            ([x, y]) =>
              `radial-gradient(ellipse 60% 50% at ${x}% ${y}%, rgba(120,80,200,0.28) 0%, transparent 70%),
               radial-gradient(ellipse 40% 60% at ${100 - x}% ${100 - y}%, rgba(60,130,200,0.18) 0%, transparent 60%),
               radial-gradient(ellipse 50% 40% at ${x * 0.6 + 20}% ${y * 0.4 + 30}%, rgba(200,120,80,0.12) 0%, transparent 50%)`
          ),
        }}
      />

      {/* Layer 2: Parallax geometric elements - deep layer */}
      <motion.div className="absolute inset-0 pointer-events-none" style={{ x: bgX, y: bgY }}>
        {/* Large ring */}
        <div
          className="absolute border border-white/[0.07] rounded-full"
          style={{ width: 500, height: 500, left: '10%', top: '5%' }}
        />
        {/* Horizontal line */}
        <div
          className="absolute h-px bg-gradient-to-r from-transparent via-white/[0.12] to-transparent"
          style={{ width: '40%', left: '50%', top: '30%' }}
        />
        {/* Small circle */}
        <div
          className="absolute w-3 h-3 rounded-full bg-purple-400/30"
          style={{ left: '80%', top: '20%' }}
        />
        {/* Arc */}
        <svg className="absolute" style={{ left: '65%', top: '60%', width: 250, height: 250 }} viewBox="0 0 200 200">
          <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
        </svg>
        {/* Second ring - bottom left */}
        <div
          className="absolute border border-white/[0.05] rounded-full"
          style={{ width: 300, height: 300, left: '-5%', bottom: '0%' }}
        />
        {/* Long horizontal rule */}
        <div
          className="absolute h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent"
          style={{ width: '30%', left: '5%', top: '72%' }}
        />
        {/* Diamond */}
        <div
          className="absolute border border-white/[0.06]"
          style={{ width: 20, height: 20, left: '42%', top: '75%', transform: 'rotate(45deg)' }}
        />
      </motion.div>

      {/* Layer 3: Mid-depth elements */}
      <motion.div className="absolute inset-0 pointer-events-none" style={{ x: midX, y: midY }}>
        {/* Vertical line */}
        <div
          className="absolute w-px bg-gradient-to-b from-transparent via-white/[0.1] to-transparent"
          style={{ height: '35%', left: '20%', top: '25%' }}
        />
        {/* Dotted cross */}
        <div className="absolute" style={{ left: '75%', top: '40%' }}>
          <div className="w-10 h-px bg-white/[0.15]" />
          <div className="w-px h-10 bg-white/[0.15] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        {/* Small ring */}
        <div
          className="absolute border border-white/[0.08] rounded-full"
          style={{ width: 140, height: 140, right: '15%', top: '15%' }}
        />
        {/* Dot cluster */}
        <div className="absolute flex gap-4" style={{ left: '30%', bottom: '25%' }}>
          <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
          <div className="w-1.5 h-1.5 rounded-full bg-white/15" />
          <div className="w-1.5 h-1.5 rounded-full bg-white/10" />
        </div>
        {/* Angle bracket */}
        <svg className="absolute" style={{ left: '8%', top: '55%', width: 40, height: 40 }} viewBox="0 0 30 30">
          <path d="M 20 5 L 5 15 L 20 25" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
        </svg>
        {/* Second cross - bottom right */}
        <div className="absolute" style={{ right: '8%', bottom: '20%' }}>
          <div className="w-8 h-px bg-white/[0.12]" />
          <div className="w-px h-8 bg-white/[0.12] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        {/* Second vertical line */}
        <div
          className="absolute w-px bg-gradient-to-b from-transparent via-white/[0.08] to-transparent"
          style={{ height: '20%', right: '25%', top: '55%' }}
        />
        {/* Triangle */}
        <svg className="absolute" style={{ left: '60%', top: '15%', width: 30, height: 30 }} viewBox="0 0 30 30">
          <path d="M 15 3 L 27 27 L 3 27 Z" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        </svg>
        {/* Dot pair */}
        <div className="absolute flex gap-6" style={{ right: '30%', top: '70%' }}>
          <div className="w-1.5 h-1.5 rounded-full bg-purple-400/20" />
          <div className="w-1.5 h-1.5 rounded-full bg-purple-400/15" />
        </div>
      </motion.div>

      {/* Layer 4: Foreground elements */}
      <motion.div className="absolute inset-0 pointer-events-none" style={{ x: fgX, y: fgY }}>
        {/* Corner accent - top right */}
        <div className="absolute" style={{ right: '10%', top: '20%' }}>
          <div className="w-20 h-px bg-gradient-to-r from-purple-400/40 to-transparent" />
          <div className="w-px h-20 bg-gradient-to-b from-purple-400/40 to-transparent" style={{ marginTop: -1 }} />
        </div>
        {/* Small accent dot */}
        <motion.div
          className="absolute w-2 h-2 rounded-full bg-purple-400/50"
          style={{ left: '45%', top: '22%' }}
          animate={{ opacity: [0.3, 0.7, 0.3] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* Diagonal line */}
        <div
          className="absolute bg-white/[0.08] origin-left"
          style={{ width: 100, height: 1, left: '55%', bottom: '30%', transform: 'rotate(-25deg)' }}
        />
        {/* Corner accent - bottom left */}
        <div className="absolute" style={{ left: '12%', bottom: '15%' }}>
          <div className="w-16 h-px bg-gradient-to-r from-transparent to-white/[0.15]" />
          <div className="w-px h-16 bg-gradient-to-b from-transparent to-white/[0.15]" style={{ marginTop: -1, marginLeft: 63 }} />
        </div>
        {/* Second pulsing dot */}
        <motion.div
          className="absolute w-1.5 h-1.5 rounded-full bg-white/30"
          style={{ right: '22%', bottom: '35%' }}
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
        />
        {/* Small diamond */}
        <div
          className="absolute border border-purple-400/20"
          style={{ width: 12, height: 12, left: '88%', top: '50%', transform: 'rotate(45deg)' }}
        />
      </motion.div>

      {/* Layer 5: Subtle noise texture */}
      <div
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '256px 256px',
        }}
      />

      {/* Layer 6: Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: "80px 80px",
        }}
      />

      {/* Layer 7: Hero content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        {/* Available badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8"
          data-testid="hero-availability-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span className="text-sm font-medium text-white/80">Available for new projects</span>
        </motion.div>

        {/* Main heading */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-['Syne'] tracking-tight leading-[0.95] mb-6"
          data-testid="hero-heading"
        >
          <span className="text-white">Turning pixels into</span>
          <br />
          <span className="relative inline-block h-[1.1em] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.span
                key={currentWordIndex}
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-100%" }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="text-gradient inline-block"
                data-testid="hero-rotating-word"
              >
                {rotatingWords[currentWordIndex]}
              </motion.span>
            </AnimatePresence>
          </span>
        </motion.h1>

        {/* Subheading */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed"
          data-testid="hero-subheading"
        >
          Design that feels inevitable — clean, fast, human.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            onClick={scrollToProjects}
            className="h-14 px-8 rounded-full bg-white text-black font-semibold text-base hover:bg-white/90 hover:scale-105 transition-all btn-shine"
            data-testid="hero-cta-projects"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            View my work
          </Button>
          <Button
            onClick={scrollToContact}
            variant="outline"
            className="no-shiny h-14 px-8 rounded-full border-white/20 text-white font-semibold text-base hover:bg-white/10 transition-all"
            data-testid="hero-cta-contact"
          >
            Get in touch
          </Button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2 text-white/40"
        >
          <span className="text-xs font-medium tracking-wider uppercase">Scroll</span>
          <ArrowDown className="w-4 h-4" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
